import React, { useState, useEffect } from 'react';
import { Search, RefreshCw, Download, Filter } from 'lucide-react';
import { SHEETS_API_URL, STATUS_CONFIG, SEVERITY_LEVELS } from '../../config/incidents';

export const IncidentHistory = ({ user }: { user: any }) => {
  const [incidents, setIncidents] = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterSev, setFilterSev] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterRegion, setFilterRegion] = useState('');

  const fetchData = async () => {
    try {
      const res  = await fetch(SHEETS_API_URL);
      const data = await res.json();
      if (data.success) setIncidents(data.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const regions = [...new Set(incidents.map((i:any) => i.region).filter(Boolean))].sort();

  const filtered = incidents.filter((i:any) => {
    const q = search.toLowerCase();
    const matchQ = !search || [i.incident_id,i.incident_type,i.city,i.reported_by,i.officer_id]
      .some(v => v?.toLowerCase?.().includes(q));
    return matchQ
      && (!filterSev    || String(i.severity) === filterSev)
      && (!filterStatus || i.status   === filterStatus)
      && (!dateFrom || (i.incident_date||'').slice(0,10) >= dateFrom)
      && (!dateTo   || (i.incident_date||'').slice(0,10) <= dateTo)
      && (!filterRegion || i.region   === filterRegion);
  });

  const sevCfg  = (v: any) => SEVERITY_LEVELS.find(s => s.value === String(v));
  const statCfg = (s: string) => STATUS_CONFIG[s] || { label: s, color: 'text-gray-400', bg: 'bg-gray-500/10' };

  const exportCSV = () => {
    const cols = ['incident_id','officer_id','incident_date','reported_by','incident_type','severity','region','city','status','response_time_minutes','approved_by','time_to_complete'];
    const rows = filtered.map((i:any) => cols.map(c => `"${i[c] ?? ''}"`).join(','));
    const csv  = [cols.join(','), ...rows].join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `nexus360_incidents_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4">
      {/* Search + filters */}
      {/* Filter bar — matches Incident Board style */}
      <div className="rounded-xl px-4 py-3 flex flex-wrap items-center gap-4 border border-blue-900/60 bg-slate-800/90 shadow-lg">
        {/* Search */}
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-400"/>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="ID, type, ville, officier..."
            className="w-full pl-8 pr-3 py-1.5 border border-slate-600 rounded-lg text-white text-xs focus:border-blue-400 focus:outline-none placeholder:text-slate-400"
            style={{backgroundColor:'#334155', caretColor:'white'}}/>
        </div>
        {/* Severity */}
        <div className="flex items-center gap-2">
          <span className="text-lg">🎯</span>
          <span className="text-xs font-semibold text-blue-300 whitespace-nowrap">Sévérité:</span>
          <select value={filterSev} onChange={e => setFilterSev(e.target.value)}
            className="px-3 py-1.5 bg-slate-700/80 border border-slate-600 rounded-lg text-white text-xs focus:outline-none focus:border-blue-400 min-w-36">
            <option value="">Toutes les sévérités</option>
            {SEVERITY_LEVELS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {/* Status */}
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <span className="text-xs font-semibold text-blue-300 whitespace-nowrap">Statut:</span>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-1.5 bg-slate-700/80 border border-slate-600 rounded-lg text-white text-xs focus:outline-none focus:border-blue-400 min-w-36">
            <option value="">Tous les statuts</option>
            {Object.entries(STATUS_CONFIG).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        {/* Region */}
        <div className="flex items-center gap-2">
          <span className="text-lg">🗺️</span>
          <span className="text-xs font-semibold text-blue-300 whitespace-nowrap">Région:</span>
          <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}
            className="px-3 py-1.5 bg-slate-700/80 border border-slate-600 rounded-lg text-white text-xs focus:outline-none focus:border-blue-400 min-w-36">
            <option value="">Toutes les régions</option>
            {regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        {/* Date range */}
        <div className="flex items-center gap-2">
          <span className="text-lg">📅</span>
          <span className="text-xs font-semibold text-blue-300 whitespace-nowrap">Période:</span>
          <input type='date' value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-3 py-1.5 bg-slate-700/80 border border-slate-600 rounded-lg text-white text-xs focus:outline-none" style={{colorScheme:'dark'}}/>
          <span className="text-blue-300 text-xs font-medium">–</span>
          <input type='date' value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-3 py-1.5 bg-slate-700/80 border border-slate-600 rounded-lg text-white text-xs focus:outline-none" style={{colorScheme:'dark'}}/>
        </div>
        {/* Reset */}
        {(search || filterSev || filterStatus || filterRegion || dateFrom || dateTo) && (
          <button onClick={resetFilters}
            className="px-3 py-1.5 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs hover:bg-red-500/20 transition-colors whitespace-nowrap">
            ✕ Réinitialiser
          </button>
        )}
        <div className="flex-1"/>
        {/* Actions */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-blue-300 font-medium">{filtered.length}/{incidents.length}</span>
          <button onClick={exportCSV}
            className="flex items-center gap-2 px-3 py-1.5 bg-gold-500/10 border border-gold-500/30 rounded-lg text-gold-500 text-xs hover:bg-gold-500/20 transition-colors whitespace-nowrap">
            <Download className="w-3.5 h-3.5"/> Export CSV
          </button>
          <button onClick={fetchData}
            className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-xs font-semibold transition-colors shadow-lg shadow-emerald-900/30 whitespace-nowrap">
            <RefreshCw className="w-3.5 h-3.5"/> Actualiser
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 border-4 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"/>
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden border border-blue-900/40 shadow-xl bg-slate-800/80">
          <div className="overflow-x-auto overflow-y-auto max-h-[600px]">
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['ID','Date','Officier','Type','Sév.','Région','Ville','Statut','Réponse(min)','Approuvé par'].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((inc:any, i:number) => {
                  const sev  = sevCfg(inc.severity);
                  const stat = statCfg(inc.status);
                  return (
                    <tr key={inc.incident_id || i}
                      className={`border-b border-blue-900/30 hover:bg-blue-800/20 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                      <td className="px-3 py-2.5 font-mono text-blue-400 whitespace-nowrap font-medium">{inc.incident_id}</td>
                      <td className="px-3 py-2.5 text-slate-200 whitespace-nowrap">{inc.incident_date?.slice?.(0,10) || inc.incident_date}</td>
                      <td className="px-3 py-2.5 text-white font-medium whitespace-nowrap">{inc.reported_by}</td>
                      <td className="px-3 py-2.5 text-white whitespace-nowrap max-w-[160px] truncate">{inc.incident_type}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${sev?.bg} ${sev?.color}`}>P{inc.severity}</span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{inc.region}</td>
                      <td className="px-3 py-2.5 text-slate-200 whitespace-nowrap">{inc.city}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 rounded-full text-xs ${stat.bg} ${stat.color}`}>{stat.label}</span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-300 text-center">{inc.response_time_minutes || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{inc.approved_by || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
